import { Box, Typography, Grid, Paper } from "@mui/material";
import { motion } from "framer-motion";
import { 
  Accessibility, 
  Visibility, 
  TrendingUp, 
  Language,
  ConnectWithoutContact,
  Public
} from "@mui/icons-material";

const AboutSection = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.8,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
    hover: { scale: 1.05, y: -5, transition: { duration: 0.2 } },
  };

  const coreValues = [
    { icon: <Accessibility />, title: "Direct Access", desc: "Eliminating traditional barriers to executive leadership" },
    { icon: <Visibility />, title: "Clear Vision", desc: "Transparent communication and strategic direction" },
    { icon: <TrendingUp />, title: "Growth Focus", desc: "Data-driven decisions for sustainable expansion" },
    { icon: <Language />, title: "Global Reach", desc: "Connecting markets across borders and cultures" },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, sm: 12, md: 18 },
        px: { xs: 2, sm: 4, md: 10, lg: 16 },
        background: "linear-gradient(135deg, #0a0a0a 0%, #111 50%, #1a1a1a 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(90deg, transparent, #C8A96B, transparent)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background: "radial-gradient(circle, rgba(200,169,107,0.03) 0%, transparent 70%)",
          borderRadius: "50%",
        },
      }}
    >
      <Grid container spacing={{ xs: 4, md: 6 }}>
        {/* Text Section - Now Full Width */}
        <Grid item xs={12}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={containerVariants}
          >
            {/* Badge / Label */}
            <motion.div variants={itemVariants}>
              <Typography
                sx={{
                  color: "#C8A96B",
                  letterSpacing: 6,
                  mb: 2,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  position: "relative",
                  display: "inline-block",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: 0,
                    width: 50,
                    height: 2,
                    background: "#C8A96B",
                  },
                }}
              >
                EXECUTIVE PROFILE
              </Typography>
            </motion.div>

            {/* Main Title */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  mb: 4,
                  fontSize: {
                    xs: "2rem",
                    sm: "2.5rem",
                    md: "3.5rem",
                    lg: "4rem",
                  },
                  lineHeight: 1.2,
                  maxWidth: "900px",
                }}
              >
                Global Leadership With{" "}
                <span style={{ color: "#C8A96B", display: "inline-block" }}>
                  Direct Access
                </span>
              </Typography>
            </motion.div>

            {/* Description 1 */}
            <motion.div variants={itemVariants}>
              <Typography
                sx={{
                  color: "#d0d0d0",
                  lineHeight: 1.8,
                  mb: 3,
                  fontSize: "1.1rem",
                  fontWeight: 400,
                  maxWidth: "800px",
                }}
              >
                John Asfar represents a modern executive approach built on
                <span style={{ color: "#C8A96B", fontWeight: 500 }}>
                  {" "}
                  accessibility, vision, strategic growth,
                </span>{" "}
                and global communication. With over two decades of leadership experience 
                across four continents, John has redefined what it means to be an accessible 
                executive in today's interconnected business landscape.
              </Typography>
            </motion.div>

            {/* Description 2 */}
            <motion.div variants={itemVariants}>
              <Typography
                sx={{
                  color: "#aaa",
                  lineHeight: 1.8,
                  fontSize: "1rem",
                  borderLeft: "3px solid #C8A96B",
                  paddingLeft: 3,
                  mt: 2,
                  mb: 5,
                  maxWidth: "750px",
                }}
              >
                This platform was created to eliminate traditional barriers and provide 
                direct communication with leadership worldwide. No gatekeepers, no bureaucracy—just 
                authentic, results-driven engagement focused on creating value and driving 
                meaningful change.
              </Typography>
            </motion.div>

            {/* Core Values Grid */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="h5"
                sx={{
                  color: "#C8A96B",
                  fontWeight: 600,
                  mb: 3,
                  fontSize: "1.5rem",
                }}
              >
                Core Principles
              </Typography>
            </motion.div>

            <Grid container spacing={3}>
              {coreValues.map((value, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    variants={cardVariants}
                    whileHover="hover"
                    custom={index}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(200,169,107,0.15)",
                        borderRadius: "16px",
                        transition: "all 0.3s ease",
                        height: "100%",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "#C8A96B",
                          background: "rgba(200,169,107,0.05)",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: "#C8A96B",
                          fontSize: 42,
                          mb: 1.5,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {value.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#fff",
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "1.1rem",
                        }}
                      >
                        {value.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#999",
                          lineHeight: 1.5,
                          display: "block",
                        }}
                      >
                        {value.desc}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Additional Impact Statement */}
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  mt: 6,
                  pt: 4,
                  borderTop: "1px solid rgba(200,169,107,0.2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <ConnectWithoutContact sx={{ color: "#C8A96B", fontSize: 40 }} />
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 500,
                      mb: 0.5,
                      fontSize: "1rem",
                    }}
                  >
                    Ready to connect with leadership?
                  </Typography>
                  <Typography
                    sx={{
                      color: "#888",
                      fontSize: "0.9rem",
                    }}
                  >
                    Direct access to strategic insights and global opportunities
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AboutSection;